<template>
    <section
        class="mx-auto flex min-h-[60vh] max-w-2xl items-center px-6 py-16"
    >
        <div
            class="w-full border-l-4 border-warning-500 bg-warning-50 p-8 shadow-sm"
        >
            <div class="mb-4 flex items-center gap-3 text-warning-800">
                <i
                    v-if="!checkTimedOut"
                    class="pi pi-spin pi-spinner text-3xl"
                />
                <i
                    v-else
                    class="pi pi-clock text-3xl"
                />
                <h1 class="text-3xl font-bold">
                    {{ t('checkout.armedPaymentTitle') }}
                </h1>
            </div>
            <p class="text-lg text-gray-700">
                {{ t('checkout.armedPaymentMessage') }}
            </p>
            <template v-if="checkTimedOut">
                <p class="mt-4 text-lg text-gray-700">
                    {{ t('checkout.armedPaymentStillPendingMessage') }}
                </p>
                <div class="mt-6 flex flex-wrap gap-3">
                    <prime-button
                        outlined
                        :label="t('checkout.keepWaiting')"
                        :disabled="isRetryingPayment"
                        @click="startPaymentCheck"
                    />
                    <prime-button
                        :label="t('checkout.tryPaymentAgain')"
                        :loading="isRetryingPayment"
                        @click="retryPayment"
                    />
                </div>
            </template>
            <template v-else>
                <p
                    class="mt-6 border-y border-warning-200 py-4 text-center text-4xl font-bold tabular-nums text-warning-900"
                    role="timer"
                >
                    {{
                        t(
                            'checkout.armedPaymentCountdown',
                            checkSecondsRemaining
                        )
                    }}
                </p>
                <p class="mt-4 text-sm font-medium text-warning-800">
                    {{ t('checkout.checkingPaymentStatus') }}
                </p>
            </template>
        </div>
    </section>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import PrimeButton from 'primevue/button'
import { useI18n } from 'vue-i18n'
import type { FragmentType } from '~/resources/graphql'
import { graphql } from '~/resources/graphql'
import { CheckoutSuccessFragment } from '~/components/Checkout/CheckoutSuccess.vue'
import { useLogError } from '~/composables/use-log-error'
import { useRpQuery } from '~/composables/graphql'
import { useSelectedLanguage } from '~/composables/use-selected-language'

const props = defineProps<{
    checkoutId: string
}>()

const emit = defineEmits<{
    (
        event: 'checkoutSuccess',
        result: FragmentType<typeof CheckoutSuccessFragment>
    ): void
    (event: 'checkoutStatusChanged'): void
}>()

// Keep the polling and retry mutations beside the customer-visible recovery state they control.
const query = useRpQuery()
const selectedLanguage = useSelectedLanguage()
const logError = useLogError()
const { t } = useI18n({ useScope: 'global' })

const PAYMENT_CHECK_MILLISECONDS = 30_000
const PAYMENT_POLL_MILLISECONDS = 2_000
const checkTimedOut = ref(false)
const checkSecondsRemaining = ref(PAYMENT_CHECK_MILLISECONDS / 1_000)
const isCheckingPayment = ref(false)
const isRetryingPayment = ref(false)
let checkDeadline = 0
let checkGeneration = 0
let checkTimer: ReturnType<typeof setTimeout> | undefined
let countdownTimer: ReturnType<typeof setInterval> | undefined

function stopPaymentCheck(): void {
    checkGeneration += 1
    if (checkTimer !== undefined) {
        clearTimeout(checkTimer)
        checkTimer = undefined
    }
    if (countdownTimer !== undefined) {
        clearInterval(countdownTimer)
        countdownTimer = undefined
    }
}

function updatePaymentCountdown(generation: number): void {
    if (generation !== checkGeneration) {
        return
    }

    const remainingMilliseconds = Math.max(0, checkDeadline - Date.now())
    checkSecondsRemaining.value = Math.ceil(remainingMilliseconds / 1_000)

    if (remainingMilliseconds === 0) {
        // The visible countdown and polling window share one deadline, so neither can outlive the other.
        checkTimedOut.value = true
        if (checkTimer !== undefined) {
            clearTimeout(checkTimer)
            checkTimer = undefined
        }
        if (countdownTimer !== undefined) {
            clearInterval(countdownTimer)
            countdownTimer = undefined
        }
    }
}

function schedulePaymentCheck(generation: number): void {
    if (generation !== checkGeneration) {
        return
    }

    const remainingMilliseconds = checkDeadline - Date.now()
    if (remainingMilliseconds <= 0) {
        updatePaymentCountdown(generation)
        return
    }

    checkTimer = setTimeout(
        () => void checkPayment(generation),
        Math.min(PAYMENT_POLL_MILLISECONDS, remainingMilliseconds)
    )
}

async function checkPayment(generation: number): Promise<void> {
    if (generation !== checkGeneration) {
        return
    }
    if (isCheckingPayment.value) {
        // A previous generation may still be finishing; keep this wait window polling once it clears.
        schedulePaymentCheck(generation)
        return
    }

    isCheckingPayment.value = true
    try {
        const result = await query(
            graphql(/* GraphQL */ `
                mutation CheckArmedCheckoutPayment(
                    $input: CheckCheckoutPaymentInput!
                    $language: Language!
                ) {
                    checkCheckoutPayment(input: $input) {
                        __typename
                        ... on Checkout {
                            status
                        }
                        ... on CommitCheckoutResult {
                            ...CheckoutSuccessFragment
                        }
                    }
                }
            `),
            {
                input: { checkoutId: props.checkoutId },
                language: selectedLanguage.value
            }
        )

        if (generation !== checkGeneration) {
            return
        }

        if (result.checkCheckoutPayment.__typename === 'CommitCheckoutResult') {
            stopPaymentCheck()
            emit('checkoutSuccess', result.checkCheckoutPayment)
            return
        }

        if (result.checkCheckoutPayment.status !== 'ARMED') {
            stopPaymentCheck()
            emit('checkoutStatusChanged')
            return
        }
    } catch (error) {
        // An uncertain status check must keep the checkout locked and continue the bounded wait.
        logError(error)
    } finally {
        isCheckingPayment.value = false
    }

    schedulePaymentCheck(generation)
}

function startPaymentCheck(): void {
    stopPaymentCheck()
    checkTimedOut.value = false
    checkDeadline = Date.now() + PAYMENT_CHECK_MILLISECONDS
    const generation = checkGeneration
    updatePaymentCountdown(generation)
    // Update more frequently than once per second so the rounded display changes at the deadline.
    countdownTimer = setInterval(() => updatePaymentCountdown(generation), 250)
    void checkPayment(generation)
}

async function retryPayment(): Promise<void> {
    stopPaymentCheck()
    isRetryingPayment.value = true
    try {
        const result = await query(
            graphql(/* GraphQL */ `
                mutation RetryArmedCheckoutPayment(
                    $input: RetryCheckoutPaymentInput!
                    $language: Language!
                ) {
                    retryCheckoutPayment(input: $input) {
                        __typename
                        ... on Checkout {
                            status
                        }
                        ... on CommitCheckoutResult {
                            ...CheckoutSuccessFragment
                        }
                    }
                }
            `),
            {
                input: { checkoutId: props.checkoutId },
                language: selectedLanguage.value
            }
        )

        if (result.retryCheckoutPayment.__typename === 'CommitCheckoutResult') {
            emit('checkoutSuccess', result.retryCheckoutPayment)
            return
        }

        if (result.retryCheckoutPayment.status === 'ARMED') {
            // An uncertain retry keeps the existing payment lock and both recovery choices visible.
            checkTimedOut.value = true
            return
        }

        emit('checkoutStatusChanged')
    } catch (error) {
        // Never infer that a failed retry request released the existing payment attempt.
        logError(error)
        checkTimedOut.value = true
    } finally {
        isRetryingPayment.value = false
    }
}

onMounted(startPaymentCheck)
onBeforeUnmount(stopPaymentCheck)
</script>
